import React from 'react';

import { graphql, Link, PageProps } from 'gatsby';
import { Link as GatsbyLink } from 'gatsby-link';

import { Button, Grid2 as Grid, styled } from '@mui/material';

import { Market } from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  Line,
  MarkdownContainer,
  ResponsiveGrid,
  Tag,
} from '@mewa/ui';

import { localeBaseUrl } from '../../../libs/alternative-links';
import t from '../../../libs/translate';
import {
  HeaderAndFooterTranslations,
  NavigationData,
} from '../../components/header/types';
import { GlobalPageContext } from '../../types';
import { StrapiImage } from '../dynamic-content-page/content-components/image';
import { DownloadSlider } from './download-slider';
import { formatDate } from './helper';
import { PressContactContainer } from './press-contact';
import { MediaDownload, PressContact, PressRelease } from './types';

export { Head } from '../../layouts/head';

type PageContext = GlobalPageContext & {
  id: string;
  market: Market;
};

type Props = NavigationData & {
  strapiPressRelease: PressRelease & {
    introduction: string;
    content: {
      data: {
        childMarkdownRemark: {
          html: string;
        };
      };
    };
    quoteName: string;
    quoteRole: string;
    quoteContent: {
      data: {
        content: string;
      };
    };
  };
  pressContact: PressContact;
  headerAndFooterTranslations: HeaderAndFooterTranslations;
  translations: {
    general_to_overview: string;
    press_release_media_section_title: string;
  };
};

export const query = graphql`
  query (
    $marketRegex: String!
    $id: String!
    $lang: String!
    $locale: String!
  ) {
    ...NavigationFragment

    strapiPressRelease(id: { eq: $id }) {
      title: Headline
      date: Date
      introduction: Introduction
      content: Content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      topics: Topics {
        topic: Topic
      }
      quoteName: QuoteName
      quoteRole: QuoteRole
      quoteContent: QuoteContent {
        data {
          content: QuoteContent
        }
      }
      mediaDownloads: MediaDownload {
        thumbnailAlternativeText: ThumbnailAlternativeText
        caption: Caption
        mediaToDownload: MediaToDownload {
          fields {
            assetUrl
          }
          extension: ext
          name
          width
          height
          mime
        }
        thumbnailImage: ThumbnailImage {
          fields {
            assetUrl
          }
          width
          height
        }
      }
    }

    pressContact: strapiPressContact(locale: { eq: $locale }) {
      title: Title
      mewa: PressContactAtMewa {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      communicationAgency: CommunicationAgency {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
    }

    translations: translation(lang: { eq: $lang }) {
      general_to_overview
      press_release_media_section_title
    }
  }
`;

// max width based on design feedback and window size of 1920px
const contentMaxWidth = '845px';

const getFirstImage = (downloads: MediaDownload[]) => {
  return downloads.filter((download) =>
    download.mediaToDownload.mime.startsWith('image/'),
  )?.[0];
};

export const PressDetail = ({
  data: { strapiPressRelease, translations, pressContact, navigationLinks },
  pageContext,
}: PageProps<Props, PageContext>) => {
  const firstImage = getFirstImage(strapiPressRelease.mediaDownloads);

  const overviewName = navigationLinks.pressOverview.name;
  const overviewUrl = `${pageContext.langPrefix}/${navigationLinks.pressOverview.slug}`;

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: navigationLinks.navigation.company.name,
      slug: `${pageContext.langPrefix}/${navigationLinks.navigation.company.slug}/`,
    },
    {
      name: overviewName,
      slug: overviewUrl,
    },
    {
      name: strapiPressRelease.title,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: overviewName,
    slug: overviewUrl,
  };

  return (
    <>
      <StyledBreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(pageContext.locale)}
        LinkComponent={GatsbyLink}
      />
      <ResponsiveGrid>
        <Grid size={12}>
          <Button
            variant="text"
            color="secondary"
            component={Link}
            sx={{ marginTop: '64px', display: ['none', 'inline-block'] }}
            to={overviewUrl}
          >
            {t(translations, 'general_to_overview')}
          </Button>
        </Grid>
        <Grid size={11}>
          <Headline>{strapiPressRelease.title}</Headline>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: 'var(--spacer-m)',
          }}
          size={12}
        >
          <DateStyled data-pagefind-meta={`date:${strapiPressRelease.date}`}>
            {formatDate(strapiPressRelease.date, pageContext.locale)}
          </DateStyled>
          <TopicWrapper>
            {strapiPressRelease.topics.map((t, i) => (
              <StyledTag key={i} tagTheme="light">
                {t.topic}
              </StyledTag>
            ))}
          </TopicWrapper>
        </Grid>
      </ResponsiveGrid>
      <ResponsiveGrid sx={{ paddingX: [0, 'var(--margin)'] }}>
        <Grid size={12}>
          <ImageWrapper
            alt={firstImage.thumbnailAlternativeText}
            image={firstImage.mediaToDownload}
            intendedForSearchIndex
          />
        </Grid>
      </ResponsiveGrid>
      <ResponsiveGrid>
        <Grid
          offset={{
            xs: 0,
            sm: 3,
            md: 4,
          }}
          size={{
            xs: 12,
            sm: 8,
            md: 6,
          }}
        >
          <IntroText>{strapiPressRelease.introduction}</IntroText>
          <PressMarkdownContainer
            sx={{ maxWidth: contentMaxWidth }}
            html={strapiPressRelease.content.data.childMarkdownRemark.html}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 10,
          }}
        >
          <Line
            sx={{
              marginTop: ['48px', '80px'],
              marginBottom: 0,
            }}
          />
        </Grid>
      </ResponsiveGrid>
      <ResponsiveGrid>
        <Grid
          sx={{ marginTop: ['48px', '82px'] }}
          size={{
            xs: 12,
            sm: 3,
            md: 4,
          }}
        >
          <QuoteName>{strapiPressRelease.quoteName}</QuoteName>
          <QuoteRole>{strapiPressRelease.quoteRole}</QuoteRole>
        </Grid>
        <Grid
          sx={{ marginTop: ['36px', '96px'] }}
          size={{
            xs: 12,
            sm: 8,
            md: 6,
          }}
        >
          {strapiPressRelease.quoteContent?.data?.content && (
            <>
              <ResponsiveGrid sx={{ paddingX: 0 }}>
                <Grid size={10}>
                  <QuoteContent>
                    {strapiPressRelease.quoteContent.data.content}
                  </QuoteContent>
                </Grid>
              </ResponsiveGrid>
              <Line
                sx={{
                  marginTop: ['48px', '100px'],
                  marginBottom: ['44px', '142px'],
                  maxWidth: contentMaxWidth,
                  marginLeft: 0,
                }}
              />
            </>
          )}
        </Grid>
      </ResponsiveGrid>
      <DownloadSlider
        downloads={strapiPressRelease.mediaDownloads}
        title={t(translations, 'press_release_media_section_title')}
      ></DownloadSlider>
      <PressContactContainer
        title={pressContact.title}
        communicationAgency={pressContact.communicationAgency}
        mewaContact={pressContact.mewa}
      ></PressContactContainer>
      <ResponsiveGrid>
        <Grid sx={{ marginTop: ['36px', '126px'] }} size={12}>
          <Line />
        </Grid>
        <Grid sx={{ textAlign: 'center' }} size={12}>
          <Button
            variant="text"
            color="secondary"
            component={Link}
            sx={{ marginTop: ['45px', '56px'], marginBottom: ['50px', '78px'] }}
            to={overviewUrl}
          >
            {t(translations, 'general_to_overview')}
          </Button>
        </Grid>
      </ResponsiveGrid>
    </>
  );
};

const PressMarkdownContainer = styled(MarkdownContainer)(({ theme }) => ({
  h2: {
    ...theme.typography.h5,
  },

  'h3, h4, h5, h6': {
    ...theme.typography.h6,
  },
}));

const QuoteName = styled('p')(({ theme }) => ({
  ...theme.typography.h6,
  margin: 0,
}));

const QuoteRole = styled('p')(({ theme }) => ({
  ...theme.typography.h6,
  fontWeight: theme.typography.fontWeightRegular,
  margin: 0,
}));

const QuoteContent = styled('p')(({ theme }) => ({
  ...theme.typography.h4,
  fontWeight: theme.typography.fontWeightRegular,
  margin: 0,
  // max width based on design feedback and window size of 1920px
  maxWidth: '700px',

  [theme.breakpoints.up('sm')]: {
    marginTop: 0,
  },
}));

PressDetail.noContactTeaser = true;

export default PressDetail;

const Headline = styled('h1')(({ theme }) => ({
  ...theme.typography.h1,
  marginTop: '64px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '80px',
  },

  [theme.breakpoints.down('md')]: {
    ...theme.typography.h2,
  },
}));

const DateStyled = styled('span')(({ theme }) => ({
  ...theme.typography.h6,
  whiteSpace: 'nowrap',
  marginRight: '18px',

  [theme.breakpoints.up('sm')]: {
    marginRight: '32px',
  },
}));

const TopicWrapper = styled('div')({
  display: 'flex',
  flexFlow: 'row wrap',
  rowGap: '5px',
});

const StyledTag = styled(Tag)({
  margin: '0 7px 15px 0',
});

const ImageWrapper = styled(StrapiImage)(() => ({ theme }) => ({
  marginTop: '38px',

  [theme.breakpoints.up('sm')]: {
    marginTop: 'var(--spacer-xxxl)',
  },
}));

const IntroText = styled('p')(({ theme }) => ({
  ...theme.typography.h4,
  marginTop: '48px',
  maxWidth: contentMaxWidth,

  [theme.breakpoints.up('sm')]: {
    marginTop: '128px',
  },
}));

const StyledBreadCrumbs = styled(BreadCrumbs)(({ theme }) => ({
  marginBottom: '2px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '9px',
    marginBottom: '9px',
  },
}));
